import { Injectable } from '@angular/core';

import {
  createDefaultDischemOrderStatusRecord,
  DISCHEM_ORDER_SUFFIX,
  DischemOrder,
  DischemPharmacy,
  MedLine,
  MedLinesRequest,
  MedLinesResponse,
  OrderResponse,
  OrderStatusRequest,
} from './models/dischem.model';
import * as moment from 'moment';
import { MedicationPrescription, ClinicalNewClient, PersonVo, UpdateDischemScript, PatientConfigurationVo } from './api-client.service';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { map, switchMap } from 'rxjs/operators';
import { MerakiClientService } from './meraki-client.service';
import { MerakiPatientService } from './meraki/meraki-patient.service';
import { Observable } from 'rxjs';
import { ConfigurationKey } from './state/patients/patients.store';
import { AngularFirestore } from '@angular/fire/firestore';
import * as _ from 'lodash';
import { dropNullUndefined } from '@app/core/functions/drop-null-undefined';
import { buildEscriptOrderNumber } from '@app/core/functions/escript-order-number';

@Injectable({
  providedIn: 'root',
})
export class DischemApiService {
  private genericHttpClient: HttpClient;

  constructor(
    private merakiClientService: MerakiClientService,
    private merakiPatientService: MerakiPatientService,
    private firestore: AngularFirestore,
    private httpHandler: HttpBackend,
    private httpClient: HttpClient,
    private clinicalNewClient: ClinicalNewClient
  ) {
    this.genericHttpClient = new HttpClient(httpHandler);
  }

  async getAuthToken(): Promise<string> {
    const token = await this.merakiClientService.getDischemActiveToken().toPromise();
    return token;
  }

  private async createMedicineLinesRequest(
    token: String,
    order: DischemOrder,
    prescription: MedicationPrescription
  ): Promise<MedLinesRequest> {
    const request = {
      order_number: order.order_number,
      originating_system_name: 'HealthBridge',
      message_id: order.message_id,
      timestamp: moment().format('hh:mm A'), // just time in hh:mm format
      med_lines: prescription.MedicationPrescriptionLines.map(
        (line, index) =>
          ({
            item_line_no: index.toString(),
            drug_name: line.MedicationDescription,
            nappi_code: line.NappiCode10 || '000' + line.NappiCode,
            //dosage: line.Quantity.toString(),
            //strength: line.Strength,
            dispense_qty: line.Quantity,
            repeat_number: line.Repeat,
            //days_supply: line.DaysSupply,
            prim_icd10_code: line.Diagnosis?.length > 0 && line.Diagnosis[0].DiagnosisCode,
            //seco_icd10_code: line.Diagnosis[].DiagnosisCode,
            directions: {
              line_one: line.Parameters['DosageDescription'],
              line_two: '',
            },
            //value: line.Value, // todo confirm what goes here
            //repeat_total: line.RepeatTotal, // optional
          }) as MedLine
      ),
    } as MedLinesRequest;

    const ref = this.getFirestoreDocForPrescription(prescription);
    ref.set({ medLines: { request: dropNullUndefined(request) } }, { merge: true });

    return request;
  }

  async sendScriptToDischem(
    tenantId: string,
    prescription: MedicationPrescription,
    pharmacy: DischemPharmacy,
    pdfUrl: string,
    encounterId: string
  ): Promise<DischemOrder> {
    const pdfBlob = await this.httpClient.get(pdfUrl, { responseType: 'blob' }).toPromise();

    const base64String = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data.split(',')[1]); // Remove data URL prefix
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(pdfBlob);
    });

    const newOrderRequest = await this.createNewOrder(prescription, pharmacy, base64String);

    const medLineRequest = await this.createMedicineLinesRequest(null, newOrderRequest, prescription);

    const request = {
      ScriptRequest: JSON.stringify(newOrderRequest),
      MedLinesRequest: JSON.stringify(medLineRequest),
    };
    const response = await this.clinicalNewClient.sendDischemScript(tenantId, encounterId, request).toPromise();

    const scriptResponse = JSON.parse(response.Data.ScriptResponse) as OrderResponse;
    const medLinesResponse = JSON.parse(response.Data.MedLinesResponse) as MedLinesResponse;

    const ref = this.getFirestoreDocForPrescription(prescription);
    ref.set(
      {
        encounterId: encounterId,
        prescriptionId: prescription.MedicationPrescriptionId,
        order: { response: dropNullUndefined(scriptResponse) },
        medLines: { response: dropNullUndefined(medLinesResponse) },
      },
      { merge: true }
    );

    if (!scriptResponse.success || !medLinesResponse.success) {
      throw new Error('Failed to send script to Dischem');
    }

    // Dis-Chem does not push status 1 (Order Received) via webhook — we write it ourselves
    // so the timeline always has a starting point immediately after a successful send.
    const sentAt = new Date().toISOString();
    const initialStatus = createDefaultDischemOrderStatusRecord(sentAt);
    ref.set(initialStatus, { merge: true });

    // Also write top-level DischemOrders doc so webhook can resolve practiceId
    const orderNumber = this.getOrderNumber(prescription);
    // Attach so the caller can persist it on SentToDischem.orderNumber
    (newOrderRequest as any).orderNumber = orderNumber;
    this.firestore
      .collection('DischemOrders')
      .doc(orderNumber)
      .set(
        {
          orderNumber,
          practiceId: prescription.Patient.PracticeId,
          patientId: prescription.Patient?.PatientId,
          encounterId,
          prescriptionId: prescription.MedicationPrescriptionId,
          clinicalPracticeRequestPath: `ClinicalPractice/${prescription.Patient.PracticeId}/DischemRequests/${orderNumber}`,
          createdAt: sentAt,
          ...initialStatus,
        },
        { merge: true }
      ); // merge: true preserves any queued status from early webhook

    return newOrderRequest;
  }

  private async createNewOrder(prescription: MedicationPrescription, pharmacy: DischemPharmacy, base64String: string) {
    const newOrderRequest = {
      originating_system_name: 'HealthBridge',
      message_id: prescription.MedicationPrescriptionId, // unique id for order
      order_number: this.getOrderNumber(prescription),
      timestamp: moment().format('hh:mm A'), // just time ? in hh:mm format?
      reference_order_number: '', // for sub orders, not usu currently
      entry_status_code: 1, // status of order
      rx_number: '', // script number, we don't have it
      process_date_time: moment().format('MM/DD/YYYY hh:mm A'), // date and time order was processed
      store_code: pharmacy.practiceNo,
      order_type: 'C', // Collection is C, Delivery is D
      main_member_details: {
        firstname: prescription.Patient.PatientAccountDetails.MedicalAidMainMemberDetails.FirstName,
        surname: prescription.Patient.PatientAccountDetails.MedicalAidMainMemberDetails.Surname,
        medical_aid: prescription.Patient.PatientAccountDetails.MedicalAidName,
        medical_aid_no: prescription.Patient.PatientAccountDetails.MedicalAidMembershipNumber,
        title: this.getTitle(prescription.Patient.PatientAccountDetails.MedicalAidMainMemberDetails),
        initials: prescription.Patient.PatientAccountDetails.MedicalAidMainMemberDetails.FirstName.charAt(0).toUpperCase(),
        dob: moment(prescription.Patient.PatientAccountDetails.MedicalAidMainMemberDetails.DateOfBirth).format('MM/DD/YYYY'),
        cell_no: prescription.Patient.PatientAccountDetails.MedicalAidMainMemberDetails.ContactNo,
        email: prescription.Patient.PatientAccountDetails.MedicalAidMainMemberDetails.EmailAddress,
        gender: this.getGender(prescription.Patient.PatientAccountDetails.MedicalAidMainMemberDetails),
        id_number: prescription.Patient.PatientAccountDetails.MedicalAidMainMemberDetails.IdentityNo,
        doctor_details: {
          name: prescription.Provider.TreatingDoctorName,
          practice_number: prescription.Provider.TreatingDoctorPracticeNumber, //todo confirm if tpn required and not bpn
          contact_number: prescription.Provider.CellphoneNumber,
        },
        address_details: {}, // mandatory, but we don't have it, how critical is it?
        postal_details: {}, // mandatory, but we don't have it, how critical is it?
      },
      dependant_details: {
        dependant_number: prescription.Patient.PatientAccountDetails.MedicalAidDependentCode,
        firstname: prescription.Patient.PatientDetails.FirstName,
        surname: prescription.Patient.PatientDetails.Surname,
        title: this.getTitle(prescription.Patient.PatientDetails),
        relation: '', // todo, Prinicpal, Spouse, Child
        medical_aid: prescription.Patient.PatientAccountDetails.MedicalAidName,
        medical_aid_no: prescription.Patient.PatientAccountDetails.MedicalAidMembershipNumber,
        id_number: prescription.Patient.PatientDetails.IdentityNo,
        dob: moment(prescription.Patient.PatientDetails.DateOfBirth).format('MM/DD/YYYY'),
        gender: this.getGender(prescription.Patient.PatientDetails),
        initials: prescription.Patient.PatientDetails.FirstName.charAt(0).toUpperCase(),
        cell_no: prescription.Patient.PatientDetails.ContactNo,
        home_tel_no: '', // todo if required
        business_tel_no: '', // todo
        address_details:
          (prescription.Patient.PhysicalAddress && {
            line_one: prescription.Patient.PhysicalAddress.Line1,
            line_two: prescription.Patient.PhysicalAddress.Line2,
            line_three: prescription.Patient.PhysicalAddress.Line3,
            area_code: prescription.Patient.PhysicalAddress.Code,
          }) ||
          null,
        postal_details:
          (prescription.Patient.PostalAddress && {
            line_one: prescription.Patient.PostalAddress.Line1,
            line_two: prescription.Patient.PostalAddress.Line2,
            line_three: prescription.Patient.PostalAddress.Line3,
            postal_code: prescription.Patient.PostalAddress.Code,
          }) ||
          null,
        doctor_details: {
          name: prescription.Provider.TreatingDoctorName,
          practice_number: prescription.Provider.TreatingDoctorPracticeNumber, //todo confirm if tpn required and not bpn
          contact_number: prescription.Provider.CellphoneNumber,
        },
      },
      recipient_details: {}, // check if need to fill,
      delivery_address_details: {}, // check if need to fill
      dispensed_patient: '', // check if need to fill
      payment_type: 'Medical Aid', // what other type is allowed
      comments: '',
      original_script_image: base64String,
      allergies: prescription.PatientAllergies?.join(','),
      generics: '', // check if need to fill
      repeat_script: '', // check if need to fill
      fridge_item_type: '', // check if need to fill
      special_instructions: '', // check if need to fill
      amount_due: '', // check if need to fill
      amount_paid: '', // check if need to fill
      longitude: 0, // check if need to fill
      latitude: 0, // check if need to fill
      delivery_date_time: '', // check if need to fill
      is_whatsapp: '', // check if need to fill
      order_source: 'HealthBridge',
      urgent: '', // check if need to fill
    } as DischemOrder;

    const ref = this.getFirestoreDocForPrescription(prescription);

    ref.set({ order: { request: dropNullUndefined(newOrderRequest) } }, { merge: true });

    return newOrderRequest;
  }

  async cancelOrder(providerId, encounterId, prescription: MedicationPrescription) {
    const storedOrderNumber = prescription.OrderNumber || this.getStoredOrderNumber(prescription);
    if (!storedOrderNumber) {
      throw new Error('Cannot cancel order: order number not found. This script was sent with an older version.');
    }
    const orderStatusRequest = {
      order_number: storedOrderNumber,
      originating_system_name: 'HealthBridge',
      message_id: prescription.MedicationPrescriptionId,
      timestamp: prescription.SentToDischem?.timestamp,
      status: 3, // 3 is cancelled
    } as OrderStatusRequest;

    const response = await this.clinicalNewClient
      .updateDischemOrder(providerId, encounterId, {
        Request: JSON.stringify(orderStatusRequest),
      } as UpdateDischemScript)
      .toPromise();
    const result = JSON.parse(response.Data) as OrderResponse;
    const ref = this.getFirestoreDocForPrescription(prescription);

    ref.set({ cancelOrder: { request: dropNullUndefined(orderStatusRequest), response: dropNullUndefined(result) } }, { merge: true });

    if (result.success != true && !result.message.includes('already')) {
      throw new Error('Failed to cancel order.');
    }

    // Dis-Chem does not push status 3 via webhook — store only cancelledAt so the
    // runtime stream in getOrderStatusStreamByOrderNumber can pre-append it without
    // polluting what Dis-Chem owns in the document.
    const cancelledAt = new Date().toISOString();
    this.firestore.collection('DischemOrders').doc(storedOrderNumber).set({ cancelledAt }, { merge: true });
  }

  getOrderNumber(prescription: MedicationPrescription): string {
    return buildEscriptOrderNumber(prescription, DISCHEM_ORDER_SUFFIX);
  }

  private getStoredOrderNumber(prescription: MedicationPrescription): string | null {
    return prescription.SentToDischem?.orderNumber || prescription.SentToDischem?.order_number || null;
  }

  /** Firestore `DischemOrders` document id — same resolution as `cancelOrder` / `getFirestoreDocForPrescription`. */
  getDischemOrderDocumentId(prescription: MedicationPrescription): string {
    return prescription.OrderNumber || this.getStoredOrderNumber(prescription) || this.getOrderNumber(prescription);
  }

  private getTitle(details: PersonVo): string {
    return details.Title || details.Gender == 'Male' ? 'Mr' : 'Ms';
  }

  private getGender(details: PersonVo): string {
    return details.Gender.substring(0, 1).toUpperCase();
  }

  private getFirestoreDocForPrescription(prescription: MedicationPrescription) {
    const orderNumber = this.getStoredOrderNumber(prescription) ?? this.getOrderNumber(prescription);
    const practiceId = prescription.Patient?.PracticeId;
    const prescriptionId = prescription.MedicationPrescriptionId;

    if (!orderNumber) {
      throw new Error(
        `DischemApiService: cannot resolve Firestore doc path — orderNumber is empty. ` +
          `prescriptionId=${prescriptionId}, practiceId=${practiceId}`
      );
    }
    if (!practiceId) {
      throw new Error(
        `DischemApiService: cannot resolve Firestore doc path — practiceId is empty. ` +
          `prescriptionId=${prescriptionId}, orderNumber=${orderNumber}`
      );
    }

    return this.firestore.collection('ClinicalPractice').doc(practiceId).collection('DischemRequests').doc(orderNumber);
  }

  pinPharmacyAsFavourite(pharmacy: DischemPharmacy, practiceTenantId: string, practiceId: string): Observable<any> {
    return this.merakiPatientService.getPatientConfigurations(practiceTenantId, practiceId).pipe(
      map(
        config =>
          config ||
          ({
            PracticeId: practiceTenantId,
            PatientId: practiceId,
            CapturedDate: new Date(),
            Configuration: {},
          } as PatientConfigurationVo)
      ),
      switchMap(config => {
        const favouritePharmacies =
          config?.Configuration[ConfigurationKey.FavouritePharmacies] != null
            ? JSON.parse(config.Configuration[ConfigurationKey.FavouritePharmacies])
            : [];

        if (!pharmacy.isFavourite) {
          const index = favouritePharmacies.findIndex(p => p.siteNo === pharmacy.siteNo);
          favouritePharmacies.splice(index, 1);
        } else {
          favouritePharmacies.push(pharmacy.siteNo);
        }

        const newConfig = {
          ...(config || {}),
          Configuration: {
            ...(config?.Configuration || {}),
            [ConfigurationKey.FavouritePharmacies]: JSON.stringify(favouritePharmacies),
          },
        };

        return this.merakiPatientService.updatePatientConfiguration(practiceTenantId, practiceId, newConfig);
      })
    );
  }

  getOrderStatusStream(prescription: MedicationPrescription): Observable<any> {
    return this.getFirestoreDocForPrescription(prescription)
      .valueChanges()
      .pipe(map((doc: any) => (doc as any) || null));
  }

  getOrderStatusStreamByOrderNumber(practiceId: string, orderNumber: string): Observable<any> {
    return this.firestore
      .collection('DischemOrders')
      .doc(orderNumber)
      .valueChanges()
      .pipe(
        map((doc: any) => {
          if (!doc) {
            return null;
          }
          const sentAt = doc.createdAt ?? new Date().toISOString();
          let history: Array<{ statusCode: number; timestamp: string }> = doc.statusHistory ?? [];

          // Always pre-append status 1 (Order Received) — Dis-Chem never pushes it
          if (!history.some(h => h.statusCode === 1)) {
            history = [{ statusCode: 1, timestamp: sentAt }, ...history];
          }

          // Always pre-append status 3 (Cancelled) when we have a cancelledAt timestamp
          // and it isn't already in the history from Dis-Chem
          if (doc.cancelledAt && !history.some(h => h.statusCode === 3)) {
            history = [...history, { statusCode: 3, timestamp: doc.cancelledAt }];
          }

          const statusCode = doc.cancelledAt ? 3 : (doc.statusCode ?? 1);
          const statusUpdatedAt = doc.cancelledAt ? doc.cancelledAt : (doc.statusUpdatedAt ?? sentAt);

          return { ...doc, statusCode, statusUpdatedAt, statusHistory: history };
        })
      );
  }
}
